import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sectionsContainer", "emptySections", "sectionsDataField"]
  static values = {
    sections: { type: Array, default: [] },
    sectionTypes: { type: Array, default: [] },
    sectionTypeLabels: { type: Object, default: {} },
    sectionTypeColors: { type: Object, default: {} },
    sectionTypeIcons: { type: Object, default: {} },
    foodCategories: { type: Array, default: [] }
  }

  connect() {
    this.renderSections()
  }

  renderSections() {
    const container = this.sectionsContainerTarget
    container.innerHTML = ''

    const sections = this.sectionsValue || []

    if (sections.length === 0) {
      this.emptySectionsTarget.style.display = 'block'
    } else {
      this.emptySectionsTarget.style.display = 'none'
      sections.forEach((section, index) => {
        container.appendChild(this.buildSectionElement(section, index))
      })
      this.resizeAllTextareas()
    }
  }

  buildSectionElement(section, sectionIndex) {
    const type = section.type || 'info'
    const color = this.sectionTypeColorsValue[type] || '#6c757d'
    const icon = this.sectionTypeIconsValue[type] || 'fa-info-circle'
    const label = this.sectionTypeLabelsValue[type] || 'Información'

    const div = document.createElement('div')
    div.className = `dietary-guide-section section-${type}`
    div.style.borderLeftColor = color
    div.dataset.sectionIndex = sectionIndex

    div.innerHTML = `
      <div class="section-header" style="background: ${color}10;">
        <div class="d-flex align-items-center">
          <i class="fas ${icon} mr-2" style="color: ${color}; font-size: 1.1rem;"></i>
          <input type="text" class="form-control form-control-sm" value="${this.escapeHtml(section.title || '')}"
                 placeholder="${label}" style="max-width: 300px; font-weight: 600; color: ${color};"
                 data-field="title" data-section="${sectionIndex}">
          <span class="badge ml-2" style="background: ${color}20; color: ${color}; border: 1px solid ${color}40; font-size: 0.7rem;">
            ${label}
          </span>
        </div>
        <div class="btn-group btn-group-sm">
          <button type="button" class="btn btn-outline-secondary btn-sm" title="Mover arriba"
                  data-action="click->dietary-guide-form#moveSectionUp" data-section="${sectionIndex}"
                  ${sectionIndex === 0 ? 'disabled' : ''}>
            <i class="fas fa-arrow-up"></i>
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" title="Mover abajo"
                  data-action="click->dietary-guide-form#moveSectionDown" data-section="${sectionIndex}"
                  ${sectionIndex === (this.sectionsValue.length - 1) ? 'disabled' : ''}>
            <i class="fas fa-arrow-down"></i>
          </button>
          <button type="button" class="btn btn-outline-danger btn-sm" title="Eliminar sección"
                  data-action="click->dietary-guide-form#removeSection" data-section="${sectionIndex}">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
      <div class="section-body">
        <div class="categories-container" data-section="${sectionIndex}">
          ${(section.categories || []).map((cat, catIdx) => this.buildCategoryHtml(cat, sectionIndex, catIdx, color)).join('')}
        </div>
        <button type="button" class="btn btn-sm btn-outline-secondary mt-2"
                data-action="click->dietary-guide-form#addCategory" data-section="${sectionIndex}">
          <i class="fas fa-plus mr-1"></i> Agregar Categoría
        </button>
        <div class="dropdown d-inline-block ml-1">
          <button type="button" class="btn btn-sm btn-outline-info dropdown-toggle mt-2"
                  data-toggle="dropdown">
            <i class="fas fa-list mr-1"></i> Categoría predefinida
          </button>
          <div class="dropdown-menu" style="max-height: 200px; overflow-y: auto;">
            ${this.foodCategoriesValue.map(cat =>
              `<a class="dropdown-item" href="#" data-action="click->dietary-guide-form#addPredefinedCategory"
                  data-section="${sectionIndex}" data-category-name="${this.escapeHtml(cat)}">${this.escapeHtml(cat)}</a>`
            ).join('')}
          </div>
        </div>
      </div>
    `

    return div
  }

  buildCategoryHtml(category, sectionIndex, catIndex, color) {
    const items = category.items || []
    return `
      <div class="dietary-guide-category" data-section="${sectionIndex}" data-category="${catIndex}">
        <div class="category-header">
          <div class="d-flex align-items-center flex-grow-1">
            <i class="fas fa-tag mr-2" style="color: ${color}; font-size: 0.8rem;"></i>
            <input type="text" class="form-control form-control-sm" value="${this.escapeHtml(category.name || '')}"
                   placeholder="Nombre de la categoría" style="max-width: 250px; font-weight: 600;"
                   data-field="category-name" data-section="${sectionIndex}" data-category="${catIndex}">
          </div>
          <button type="button" class="btn btn-outline-danger btn-sm" title="Eliminar categoría"
                  data-action="click->dietary-guide-form#removeCategory"
                  data-section="${sectionIndex}" data-category="${catIndex}">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="items-container" data-section="${sectionIndex}" data-category="${catIndex}">
          ${items.map((item, itemIdx) => this.buildItemHtml(item, sectionIndex, catIndex, itemIdx)).join('')}
        </div>
        <button type="button" class="btn btn-sm btn-link text-success mt-1 p-0"
                data-action="click->dietary-guide-form#addItem"
                data-section="${sectionIndex}" data-category="${catIndex}">
          <i class="fas fa-plus mr-1"></i> Agregar alimento
        </button>
      </div>
    `
  }

  buildItemHtml(item, sectionIndex, catIndex, itemIndex) {
    const ds = `data-section="${sectionIndex}" data-category="${catIndex}" data-item="${itemIndex}"`
    return `
      <div class="dietary-guide-item" ${ds}>
        <textarea class="form-control form-control-sm item-name" rows="1"
               placeholder="Nombre del alimento"
               data-field="item-name" ${ds}
               data-action="input->dietary-guide-form#autoGrow">${this.escapeHtml(item.name || '')}</textarea>
        <textarea class="form-control form-control-sm item-note" rows="1"
               placeholder="Nota (opcional)"
               data-field="item-note" ${ds}
               data-action="input->dietary-guide-form#autoGrow">${this.escapeHtml(item.note || '')}</textarea>
        <div class="dietary-guide-item-actions">
          <div class="custom-control custom-checkbox" title="Destacar">
            <input type="checkbox" class="custom-control-input" id="highlight_${sectionIndex}_${catIndex}_${itemIndex}"
                   ${item.highlight ? 'checked' : ''}
                   data-field="item-highlight" ${ds}>
            <label class="custom-control-label" for="highlight_${sectionIndex}_${catIndex}_${itemIndex}">
              <i class="fas fa-star" style="font-size: 0.7rem;"></i>
            </label>
          </div>
          <button type="button" class="btn btn-outline-secondary btn-sm" title="Copiar texto"
                  data-action="click->dietary-guide-form#copyItem" ${ds}>
            <i class="fas fa-copy" style="font-size: 0.7rem;"></i>
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" title="Pegar texto"
                  data-action="click->dietary-guide-form#pasteItem" ${ds}>
            <i class="fas fa-paste" style="font-size: 0.7rem;"></i>
          </button>
          <button type="button" class="btn btn-outline-danger btn-sm" title="Eliminar"
                  data-action="click->dietary-guide-form#removeItem" ${ds}>
            <i class="fas fa-times" style="font-size: 0.7rem;"></i>
          </button>
        </div>
      </div>
    `
  }

  addSection(event) {
    event.preventDefault()
    const type = event.currentTarget.dataset.sectionType
    const sections = [...this.sectionsValue]
    sections.push({
      type: type,
      title: '',
      categories: []
    })
    this.sectionsValue = sections
    this.renderSections()
  }

  removeSection(event) {
    event.preventDefault()
    const idx = parseInt(event.currentTarget.dataset.section)
    if (!confirm('¿Eliminar esta sección?')) return
    this.collectDataFromDOM()
    const sections = [...this.sectionsValue]
    sections.splice(idx, 1)
    this.sectionsValue = sections
    this.renderSections()
  }

  moveSectionUp(event) {
    event.preventDefault()
    const idx = parseInt(event.currentTarget.dataset.section)
    if (idx === 0) return
    this.collectDataFromDOM()
    const sections = [...this.sectionsValue]
    ;[sections[idx - 1], sections[idx]] = [sections[idx], sections[idx - 1]]
    this.sectionsValue = sections
    this.renderSections()
  }

  moveSectionDown(event) {
    event.preventDefault()
    const idx = parseInt(event.currentTarget.dataset.section)
    const sections = [...this.sectionsValue]
    if (idx >= sections.length - 1) return
    this.collectDataFromDOM()
    ;[sections[idx], sections[idx + 1]] = [sections[idx + 1], sections[idx]]
    this.sectionsValue = sections
    this.renderSections()
  }

  addCategory(event) {
    event.preventDefault()
    const sectionIdx = parseInt(event.currentTarget.dataset.section)
    this.collectDataFromDOM()
    const sections = [...this.sectionsValue]
    if (!sections[sectionIdx].categories) sections[sectionIdx].categories = []
    sections[sectionIdx].categories.push({ name: '', items: [{ name: '', note: '', highlight: false }] })
    this.sectionsValue = sections
    this.renderSections()
  }

  addPredefinedCategory(event) {
    event.preventDefault()
    const sectionIdx = parseInt(event.currentTarget.dataset.section)
    const catName = event.currentTarget.dataset.categoryName
    this.collectDataFromDOM()
    const sections = [...this.sectionsValue]
    if (!sections[sectionIdx].categories) sections[sectionIdx].categories = []
    sections[sectionIdx].categories.push({ name: catName, items: [{ name: '', note: '', highlight: false }] })
    this.sectionsValue = sections
    this.renderSections()
  }

  removeCategory(event) {
    event.preventDefault()
    const sectionIdx = parseInt(event.currentTarget.dataset.section)
    const catIdx = parseInt(event.currentTarget.dataset.category)
    this.collectDataFromDOM()
    const sections = [...this.sectionsValue]
    sections[sectionIdx].categories.splice(catIdx, 1)
    this.sectionsValue = sections
    this.renderSections()
  }

  addItem(event) {
    event.preventDefault()
    const sectionIdx = parseInt(event.currentTarget.dataset.section)
    const catIdx = parseInt(event.currentTarget.dataset.category)
    this.collectDataFromDOM()
    const sections = [...this.sectionsValue]
    if (!sections[sectionIdx].categories[catIdx].items) sections[sectionIdx].categories[catIdx].items = []
    sections[sectionIdx].categories[catIdx].items.push({ name: '', note: '', highlight: false })
    this.sectionsValue = sections
    this.renderSections()
  }

  removeItem(event) {
    event.preventDefault()
    const sectionIdx = parseInt(event.currentTarget.dataset.section)
    const catIdx = parseInt(event.currentTarget.dataset.category)
    const itemIdx = parseInt(event.currentTarget.dataset.item)
    this.collectDataFromDOM()
    const sections = [...this.sectionsValue]
    sections[sectionIdx].categories[catIdx].items.splice(itemIdx, 1)
    this.sectionsValue = sections
    this.renderSections()
  }

  toggleTemplate(event) {
    const patientSelect = this.element.querySelector('[name="dietary_guide[patient_id]"]')
    const templateCheckbox = this.element.querySelector('#is_template_checkbox')

    if (event.target === templateCheckbox || event.target.id === 'is_template_checkbox') {
      if (templateCheckbox.checked && patientSelect) {
        patientSelect.value = ''
      }
    } else if (event.target === patientSelect || event.target.name === 'dietary_guide[patient_id]') {
      if (patientSelect.value && templateCheckbox) {
        templateCheckbox.checked = false
      }
    }
  }

  loadTemplate(event) {
    const select = event.target
    const option = select.options[select.selectedIndex]

    if (!option.value) return

    if (!confirm('¿Cargar el contenido de esta plantilla? Se sobrescribirán las secciones actuales.')) {
      select.value = ''
      return
    }

    const sectionsJson = option.dataset.sections
    const observations = option.dataset.observations || ''
    const footnotes = option.dataset.footnotes || ''
    const dietRestrictionId = option.dataset.dietRestrictionId || ''

    try {
      this.sectionsValue = JSON.parse(sectionsJson || '[]')
    } catch (e) {
      this.sectionsValue = []
    }

    // Fill observations and footnotes
    const obsField = this.element.querySelector('[name="dietary_guide[observations]"]')
    if (obsField && observations) obsField.value = observations

    const footField = this.element.querySelector('[name="dietary_guide[footnotes]"]')
    if (footField && footnotes) footField.value = footnotes

    const drField = this.element.querySelector('[name="dietary_guide[diet_restriction_id]"]')
    if (drField && dietRestrictionId) drField.value = dietRestrictionId

    this.renderSections()
    select.value = ''
    this.showNotification('Plantilla cargada correctamente', 'success')
  }

  collectDataFromDOM() {
    const sections = [...this.sectionsValue]
    const container = this.sectionsContainerTarget

    container.querySelectorAll('[data-field="title"]').forEach(input => {
      const sIdx = parseInt(input.dataset.section)
      if (sections[sIdx]) sections[sIdx].title = input.value
    })

    container.querySelectorAll('[data-field="category-name"]').forEach(input => {
      const sIdx = parseInt(input.dataset.section)
      const cIdx = parseInt(input.dataset.category)
      if (sections[sIdx]?.categories?.[cIdx]) sections[sIdx].categories[cIdx].name = input.value
    })

    container.querySelectorAll('[data-field="item-name"]').forEach(input => {
      const sIdx = parseInt(input.dataset.section)
      const cIdx = parseInt(input.dataset.category)
      const iIdx = parseInt(input.dataset.item)
      if (sections[sIdx]?.categories?.[cIdx]?.items?.[iIdx]) {
        sections[sIdx].categories[cIdx].items[iIdx].name = input.value
      }
    })

    container.querySelectorAll('[data-field="item-note"]').forEach(input => {
      const sIdx = parseInt(input.dataset.section)
      const cIdx = parseInt(input.dataset.category)
      const iIdx = parseInt(input.dataset.item)
      if (sections[sIdx]?.categories?.[cIdx]?.items?.[iIdx]) {
        sections[sIdx].categories[cIdx].items[iIdx].note = input.value
      }
    })

    container.querySelectorAll('[data-field="item-highlight"]').forEach(input => {
      const sIdx = parseInt(input.dataset.section)
      const cIdx = parseInt(input.dataset.category)
      const iIdx = parseInt(input.dataset.item)
      if (sections[sIdx]?.categories?.[cIdx]?.items?.[iIdx]) {
        sections[sIdx].categories[cIdx].items[iIdx].highlight = input.checked
      }
    })

    this.sectionsValue = sections
  }

  serializeSections(event) {
    this.collectDataFromDOM()
    const field = this.sectionsDataFieldTarget
    field.value = JSON.stringify(this.sectionsValue)
  }

  autoGrow(event) {
    this.resizeTextarea(event.target)
  }

  resizeTextarea(el) {
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  resizeAllTextareas() {
    this.sectionsContainerTarget
      .querySelectorAll('textarea.item-name, textarea.item-note')
      .forEach(el => this.resizeTextarea(el))
  }

  itemNameFromButton(button) {
    const row = button.closest('.dietary-guide-item')
    return row ? row.querySelector('.item-name') : null
  }

  copyItem(event) {
    event.preventDefault()
    const textarea = this.itemNameFromButton(event.currentTarget)
    if (!textarea) return
    const text = textarea.value || ''

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => this.showNotification('Texto copiado', 'success'))
        .catch(() => this.fallbackCopy(textarea))
    } else {
      this.fallbackCopy(textarea)
    }
  }

  fallbackCopy(textarea) {
    textarea.focus()
    textarea.select()
    try {
      document.execCommand('copy')
      this.showNotification('Texto copiado', 'success')
    } catch (e) {
      this.showNotification('No se pudo copiar. Usá Ctrl+C', 'warning')
    }
  }

  pasteItem(event) {
    event.preventDefault()
    const target = this.itemNameFromButton(event.currentTarget)
    if (!target) return

    if (navigator.clipboard && navigator.clipboard.readText) {
      navigator.clipboard.readText()
        .then(text => this.insertIntoTextarea(target, text))
        .catch(() => {
          target.focus()
          this.showNotification('No se pudo pegar. Usá Ctrl+V', 'warning')
        })
    } else {
      target.focus()
      this.showNotification('Usá Ctrl+V para pegar', 'info')
    }
  }

  insertIntoTextarea(target, text) {
    const start = target.selectionStart ?? target.value.length
    const end = target.selectionEnd ?? target.value.length
    target.value = target.value.slice(0, start) + text + target.value.slice(end)
    const caret = start + text.length
    target.setSelectionRange(caret, caret)
    this.resizeTextarea(target)
    this.collectDataFromDOM()
    target.focus()
    this.showNotification('Texto pegado', 'success')
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div')
    notification.className = `alert alert-${type} alert-dismissible fade show position-fixed`
    notification.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 250px;'
    notification.innerHTML = `
      ${message}
      <button type="button" class="close" data-dismiss="alert">
        <span>&times;</span>
      </button>
    `
    document.body.appendChild(notification)
    setTimeout(() => notification.remove(), 3000)
  }
}
